import SideSheetModal from "@/components/SideSheetModal";
import { ColumnSort } from "@tanstack/react-table";
import Icon from "@/components/Icon";
import Button from "@/components/Button";

/**
 * Represents a single sort option in the mobile sort side sheet
 * @property id - The column ID to sort by (must match column definition)
 * @property label - The column label to display (e.g., "Event Name")
 * @property desc - Whether this sorts descending (true) or ascending (false)
 * @property display - User-friendly description (e.g., "A to Z", "New to Old")
 */
export interface SortOption {
  id: string;
  label: string;
  desc: boolean;
  display: string;
}

/**
 * Props for MobileSortSideSheet component
 * @property isOpen - Whether the side sheet is currently open
 * @property onClose - Callback when side sheet should close
 * @property onSortChange - Callback when user selects a sort option
 * @property currentSort - Current active sort state from table
 * @property sortOptions - Array of available sort options to display
 */
export interface MobileSortSideSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onSortChange: (sort: ColumnSort[]) => void;
  currentSort: ColumnSort[];
  sortOptions: SortOption[];
}

/**
 * MobileSortSideSheet provides a consistent mobile UI for table sorting.
 *
 * Displays a side sheet modal with a list of sort options. Users can:
 * - See all available sort options (column + direction)
 * - See which option is currently active (checkmark)
 * - Select a new sort option (applies immediately and closes side sheet)
 *
 * **Usage:**
 * ```tsx
 * const [sorting, setSorting] = useState<SortingState>([{ id: "name", desc: false }]);
 * const [mobileSortOpen, setMobileSortOpen] = useState(false);
 *
 * const sortOptions: SortOption[] = [
 *   { id: "name", label: "Name", desc: false, display: "A to Z" },
 *   { id: "name", label: "Name", desc: true, display: "Z to A" },
 *   { id: "date", label: "Date", desc: true, display: "Newest First" },
 *   { id: "date", label: "Date", desc: false, display: "Oldest First" }
 * ];
 *
 * <TableControls
 *   onSortClick={() => setMobileSortOpen(true)}
 *   // ...
 * />
 *
 * <MobileSortSideSheet
 *   isOpen={mobileSortOpen}
 *   onClose={() => setMobileSortOpen(false)}
 *   onSortChange={setSorting}
 *   currentSort={sorting}
 *   sortOptions={sortOptions}
 * />
 * ```
 *
 * @example
 * // Common display patterns:
 * // Text: "A to Z", "Z to A"
 * // Numbers: "Lowest to Highest", "Highest to Lowest"
 * // Dates: "Newest First", "Oldest First"
 * // Counts: "Most to Least", "Least to Most"
 */
export default function MobileSortSideSheet({
  isOpen,
  onClose,
  onSortChange,
  currentSort,
  sortOptions
}: MobileSortSideSheetProps) {
  const handleSortSelect = (option: SortOption) => {
    onSortChange([{ id: option.id, desc: option.desc }]);
    onClose();
  };

  const isCurrentSort = (option: SortOption) => {
    return currentSort.length > 0 && currentSort[0].id === option.id && currentSort[0].desc === option.desc;
  };

  const handleSidebarChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  return (
    <SideSheetModal
      isSidebarOpen={isOpen}
      setSidebarOpen={handleSidebarChange}
      title="Sort Table By"
      disableFocusManagement={true}
      actions={
        <Button variant="filled" onClick={onClose}>
          Close
        </Button>
      }
    >
      <div className="space-y-0 mt-4">
        {sortOptions.map((option) => (
          <button
            key={`${option.id}-${option.desc}`}
            onClick={() => handleSortSelect(option)}
            className="w-full flex items-center justify-between py-4 text-left hover:bg-sys-surface-hover transition-colors border-b border-sys-outline-variant last:border-b-0"
            data-testid={`sort-option-${option.id}-${option.desc ? "desc" : "asc"}`}
          >
            <div className="flex flex-col">
              <span className="text-label-large text-sys-on-surface">{option.label}</span>
              <span className="text-body-medium text-sys-on-surface-variant">{option.display}</span>
            </div>
            {isCurrentSort(option) && <Icon name="check" className="text-sys-primary ml-3" />}
          </button>
        ))}
      </div>
    </SideSheetModal>
  );
}
